import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { DOCUMENT } from '@angular/common';
import { ApplicationRef, Component, Inject, NgZone, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { NgForm } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { environment } from './../environments/environment';
import { TITLES } from './core/data/app.data';
import { UsageService } from './core/usage/usage.service';
import { ContactService } from './core/services/contact.service';
import { FeedbackService } from './core/services/feedback.service';
import { GroupService } from './core/services/group.service';
import { IdleService } from './core/services/idle.service';
import { LaunchDetectionService } from './core/services/launch-detection.service';
import { LoggerService } from './core/services/logger.service';
import { SocketService } from './core/services/socket.service';
import { SubscriptionService } from './core/services/subscription.service';
import { ThemeService } from './core/services/theme.service';
import { UpdateService } from './core/services/update.service';
import { UserService } from './core/services/user.service';
import { InfoDialogComponent } from './shared/components/info-dialog/info-dialog.component';
import { ColleagueService } from './core/services/colleague.service';
//import { TutorialService } from './core/tutorial/tutorial.service';
import { PatientService } from './core/services/patient.service';
import { PatientFinancialService } from './core/services/patient-financial.service';
import { YocoPaymentProcessorService } from './core/services/yoco-payment-processor.service';
import { PatientNoteService } from './core/services/patient-note.service';
import { TagService } from './core/services/tag.service';
import { PatientAiService } from './core/services/patient-ai.service';
import { HospitalService } from './core/services/hospital.service';
import { AIService } from './core/services/ai.service';
import { ScreenWakeService } from './core/services/screen-wake.service';
import { PatientPrescriptionService } from './core/services/patient-prescription.service';
import { PatientConsentService } from './core/services/patient-consent.service';
import { PatientLabService } from './core/services/patient-lab.service';
import { PullToRefreshService } from './core/services/pull-to-refresh.service';

// Type definition for pageshow event with persisted property
interface PageTransitionEvent extends Event {
  persisted: boolean;
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
  year = new Date().getFullYear();
  appVersion = environment.APP_VERSION;
  isMobile: boolean = false;
  isTablet: boolean = false;
  launchMode?: string;
  currentUser?: any;
  titles = TITLES

  contactMessage: any = {};
  @ViewChild('contactDialog') contactDialog?: TemplateRef<any>;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private breakpointObserver: BreakpointObserver,
    private launchDetectionService: LaunchDetectionService,
    private dialog: MatDialog,
    private feedbackService: FeedbackService,
    private contactService: ContactService,
    private router: Router,
    // Starts usage tracking for REMOAT once someone is signed in.
    private usage: UsageService,
    private user: UserService,
    private patientService: PatientService,
    // Imported the services here to ensure they get initialized early. DO NOT REMOVE!
    private theme: ThemeService,
    private updateService: UpdateService,
    private logger: LoggerService,
    private idle: IdleService,
    private socket: SocketService,
    private group: GroupService,
    private subscription: SubscriptionService,
    private colleagues: ColleagueService,
    //private tutorial: TutorialService,
    private financial: PatientFinancialService,
    private yoco: YocoPaymentProcessorService,
    private notes: PatientNoteService,
    private tags: TagService,
    private patientAi: PatientAiService,
    private hospital: HospitalService,
    private ai: AIService,
    private wake: ScreenWakeService,
    private pullToRefresh: PullToRefreshService,
    private prescription: PatientPrescriptionService,
    private consent: PatientConsentService,
    //private tutorialService: TutorialService,
    private patientFinancialService: PatientFinancialService,
    private yocoPaymentProcessorService: YocoPaymentProcessorService,
    private patientLabService: PatientLabService,
    private ngZone: NgZone,
    private applicationRef: ApplicationRef
  ) {

    this.breakpointObserver.observe([Breakpoints.HandsetPortrait, Breakpoints.HandsetLandscape, Breakpoints.TabletPortrait])
      .subscribe(result => {
        this.isMobile = result.matches;
        this.updateBodyClass();
      });

    this.breakpointObserver.observe([Breakpoints.TabletLandscape])
      .subscribe(result => {
        this.isTablet = result.matches;
        this.updateBodyClass();
      });

    this.user.userProfile$
      .subscribe(result => this.currentUser = result);


  }

  ngOnInit(): void {
    this.applyNativePlatformChrome();
    this.pullToRefresh.init();

    this.launchMode = this.launchDetectionService.getLaunchMode();
    console.log('App launched as:', this.launchMode);

    // The `IMPORTANT NOTICE` dialog that used to open here — driven by the hardcoded browser/OS
    // list in `launchDetectionService.getUserAlerts()` — was REMOVED by
    // `tasks/announcements.md` Phase F5. It is the hardcoded ancestor of the announcements
    // feature, and two boot-time modals must not be able to stack over each other.
    //
    // Its replacement is server-driven: `DashboardComponent` opens the announcement dialog once
    // the user profile and group have resolved, and only when `AnnouncementGateService` says
    // interrupting is safe. Anything the old list carried is re-created as an `info` announcement
    // in the admin dashboard.

    // Handle mobile browser page restoration from cache (back/forward cache)
    // This is critical for mobile browsers that aggressively cache pages
    window.addEventListener('pageshow', (event: PageTransitionEvent) => {
      // Check if page was restored from cache (back/forward cache)
      if (event.persisted) {
        console.log('Page restored from cache - forcing reinitialization');
        // Force Angular to detect changes within NgZone
        this.ngZone.run(() => {
          // Trigger change detection to ensure UI updates
          this.applicationRef.tick();
        });
        
        // Reconnect socket if needed (socket may have been disconnected during background)
        this.socket.reconnect();
        
        // Force router to re-evaluate current route to ensure components are properly initialized
        const currentUrl = this.router.url;
        this.ngZone.run(() => {
          this.router.navigateByUrl(currentUrl, { skipLocationChange: true }).then(() => {
            console.log('Route re-evaluated after cache restoration');
          });
        });
      }
    });
  }

  /**
   * Native-only chrome adjustments: tag the body so SCSS rules can opt in to
   * safe-area handling, and enable viewport-fit=cover so env(safe-area-inset-*)
   * resolves to non-zero values inside the WKWebView. No-op on the web build.
   */
  private applyNativePlatformChrome(): void {
    if (!Capacitor.isNativePlatform()) return;

    this.document.body.classList.add('native-platform');
    this.document.body.classList.add(`platform-${Capacitor.getPlatform()}`);

    const viewport = this.document.querySelector('meta[name="viewport"]');
    if (viewport) {
      viewport.setAttribute(
        'content',
        'width=device-width, initial-scale=1, viewport-fit=cover'
      );
    }
  }

  updateBodyClass(): void {
    const classMap: { [key: string]: boolean } = {
      'mobile': this.isMobile,
      'tablet': this.isTablet,
      'demo': typeof window !== 'undefined' && window.location.href.indexOf('demo.medicai.co.za') !== -1
    };

    Object.keys(classMap).forEach((className: string) => {
      if (classMap[className]) {
        this.document.body.classList.add(className);
      } else {
        this.document.body.classList.remove(className);
      }
    });
  }


  /** Send is allowed once the challenge is solved; skipped when Turnstile isn't configured yet. */
  get contactChallengeReady(): boolean {
    if (!environment.TURNSTILE_SITE_KEY) return true;
    return !!this.contactMessage?.turnstileToken;
  }

  openContactDialog(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.patientService.isRecordingLocked()) {
      return;
    }
    const user = this.currentUser;
    // `company` is the honeypot: hidden from users, left empty; bots that fill it are dropped server-side.
    // The <app-turnstile> widget inside the dialog renders/cleans up with the dialog lifecycle;
    // it emits the token via (tokenChange) into contactMessage.turnstileToken.
    this.contactMessage = { title: user?.title || '', name: user?.name || '', surname: user?.surname || '', email: user?.email || '', phone: '', message: '', company: '', turnstileToken: '' };
    this.dialog.open(this.contactDialog!, { width: '600px' });
  }

  /**
   * Guarded as well as gated: the button's [disabled] is the primary control, but the guard keeps
   * an incomplete or unchallenged message from being posted by any other path.
   */
  sendContactMessage(form?: NgForm): void {
    if (form?.invalid || !this.contactChallengeReady) {
      this.feedbackService.showToast('Please complete all required fields before sending.');
      return;
    }

    const sub = this.contactService.sendContactEmail(this.contactMessage)
      .subscribe(
        (response: any) => {
          this.feedbackService.showToast('Your contact request has been sent successfully.');
          sub.unsubscribe();
        },
        (error) => {
          this.feedbackService.showToast('There was an error submitting your contact request. Please try again.');
          sub.unsubscribe();
        }
      );
    this.dialog.closeAll(); // Close the edit colleague dialog
  }

}
